'use client';

import React, { useEffect, useState } from 'react';

/**
 * OfflineBanner — Phase 24 PWA
 * Shows a small branded banner when the device loses internet access.
 * Disappears automatically when connection is restored.
 * Never blocks navigation or fabricates data.
 */
export default function OfflineBanner() {
  const [isOffline, setIsOffline] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    // Read initial state after mount (avoids SSR mismatch)
    setIsOffline(!navigator.onLine);

    const handleOffline = () => setIsOffline(true);
    const handleOnline = () => setIsOffline(false);

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  if (!mounted || !isOffline) return null;

  return (
    <div
      role="alert"
      aria-live="polite"
      className="fixed top-0 left-0 right-0 z-[200] flex items-center justify-between gap-3 px-4 py-2.5"
      style={{
        background: '#1B6B3A',
        color: '#fff',
        paddingTop: 'max(10px, env(safe-area-inset-top, 10px))',
      }}
    >
      <div className="flex items-center gap-2 min-w-0">
        {/* Wifi-off icon (inline SVG — no extra dep) */}
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="flex-shrink-0 opacity-90"
          aria-hidden="true"
        >
          <line x1="1" y1="1" x2="23" y2="23" />
          <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
          <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
          <path d="M10.71 5.05A16 16 0 0 1 22.56 9" />
          <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
          <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
          <line x1="12" y1="20" x2="12.01" y2="20" />
        </svg>
        <span className="text-sm font-medium truncate">
          No internet connection — please check your network
        </span>
      </div>
      <button
        onClick={() => window.location?.reload()}
        className="flex-shrink-0 text-xs font-semibold underline underline-offset-2 opacity-90 hover:opacity-100 min-h-[36px] px-1"
        aria-label="Retry connection"
      >
        Retry
      </button>
    </div>
  );
}
